import React, { useRef } from "react";
import { Box, ButtonBase, Paper, Typography } from "@mui/material";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import TuneIcon from "@mui/icons-material/Tune";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import { useTheme } from "../../theme/ThemeContext";
import { PAPER_MODES } from "../../theme/themePresets";

export const shouldShowMobileReaderBar = ({
  isMobile = false,
  isZenMode = false,
  hasSelection = false,
  speechStatus = "idle",
  scrollDirection = "idle",
  scrollPercent = 0,
}) => {
  if (!isMobile || isZenMode || hasSelection) return false;
  if (speechStatus === "playing" || speechStatus === "paused") return false;
  if (scrollDirection === "down" && scrollPercent > 5) return false;
  return true;
};

const MobileBarItem = ({ icon, label, onClick, disabled = false, active = false }) => (
  <ButtonBase
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    sx={{
      flex: 1,
      py: 0.85,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 0.25,
      color: active ? "primary.main" : "text.primary",
      "&.Mui-disabled": {
        opacity: 0.35,
      },
    }}
  >
    {icon}
    <Typography
      sx={{
        fontSize: "0.68rem",
        fontWeight: active ? 700 : 500,
        lineHeight: 1.1,
      }}
    >
      {label}
    </Typography>
  </ButtonBase>
);

const MobileReaderBottomBar = ({
  isMobile = false,
  isZenMode = false,
  hasSelection = false,
  speechStatus = "idle",
  scrollDirection = "idle",
  scrollPercent = 0,
  hasPrev = false,
  hasNext = false,
  onOpenCatalog,
  onPrev,
  onOpenSettings,
  onNext,
  edition,
}) => {
  const { paperMode, setPaperMode } = useTheme();
  const lastLightRef = useRef("sepia");
  const isEn = edition?.id === "en";
  const ui = edition?.ui || {};

  const currentPaper = PAPER_MODES.find((m) => m.id === paperMode);
  const isNight = Boolean(currentPaper?.isDark);

  const handleToggleNight = () => {
    if (isNight) {
      setPaperMode(lastLightRef.current || "sepia");
    } else {
      if (paperMode) lastLightRef.current = paperMode;
      setPaperMode("dark");
    }
  };

  const visible = shouldShowMobileReaderBar({
    isMobile,
    isZenMode,
    hasSelection,
    speechStatus,
    scrollDirection,
    scrollPercent,
  });

  if (!isMobile) return null;

  return (
    <Paper
      elevation={8}
      data-testid="mobile-reader-bottom-bar"
      aria-hidden={!visible}
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1200,
        pb: "env(safe-area-inset-bottom)",
        borderTop: "1px solid",
        borderColor: "divider",
        bgcolor: (t) =>
          t.palette.mode === "dark"
            ? "rgba(18, 23, 30, 0.94)"
            : "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(12px)",
        transform: visible ? "translateY(0)" : "translateY(110%)",
        transition: "transform 220ms cubic-bezier(0.22, 1, 0.36, 1)",
        pointerEvents: visible ? "auto" : "none",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", height: 54 }}>
        <MobileBarItem
          icon={<MenuBookIcon sx={{ fontSize: 20 }} />}
          label={ui.railCatalog || (isEn ? "Catalog" : "目录")}
          onClick={onOpenCatalog}
        />
        <MobileBarItem
          icon={<NavigateBeforeIcon sx={{ fontSize: 22 }} />}
          label={ui.prevChapter || (isEn ? "Prev" : "上一章")}
          disabled={!hasPrev}
          onClick={onPrev}
        />
        <MobileBarItem
          icon={<TuneIcon sx={{ fontSize: 20 }} />}
          label={ui.railSettings || (isEn ? "Settings" : "设置")}
          onClick={(e) => onOpenSettings && onOpenSettings(e.currentTarget)}
        />
        <MobileBarItem
          icon={<NavigateNextIcon sx={{ fontSize: 22 }} />}
          label={ui.nextChapter || (isEn ? "Next" : "下一章")}
          disabled={!hasNext}
          onClick={onNext}
        />
        <MobileBarItem
          icon={
            isNight ? (
              <LightModeOutlinedIcon sx={{ fontSize: 20 }} />
            ) : (
              <DarkModeOutlinedIcon sx={{ fontSize: 20 }} />
            )
          }
          label={isNight ? (isEn ? "Day" : "日间") : isEn ? "Night" : "夜间"}
          active={Boolean(isNight)}
          onClick={handleToggleNight}
        />
      </Box>
    </Paper>
  );
};

export default MobileReaderBottomBar;
